import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  TEXT_LINK_LAYER,
  addLink,
  canLink,
  chainContaining,
  chainMembersFrom,
  hasOutgoingLink,
  nextUidInChain,
  prevUidInChain,
  removeBoxFromChains,
  removeLinkAfter,
} from '@/lib/text-linking/text-chain-mutator'
import { buildLinkedShapeFrame } from '@/lib/text-linking/build-linked-text-frame'
import {
  chainSeamsFromMemo,
  changedSlices,
  recordChainSeams,
  storyFromChain,
} from '@/lib/text-linking/chain-story'
import { canEditChainFrameContent } from '@/lib/text-linking/chain-content-edit-policy'
import {
  type ChainCaretFrame,
  locateChainCaretDestination,
} from '@/lib/text-linking/locate-chain-caret-destination'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { collectLinkableFrames, isShapeBackedFrame } from '@/lib/text-linking/linkable-text-frame'
import { paragraphsHaveText } from '@/lib/text-linking/merge-overflow-paragraphs'
import { deleteTrailingCharacter } from '@/lib/text-linking/delete-trailing-character'
import { measureShapeParagraphsPlainTextLength } from '@/lib/text-linking/measure-paragraphs-plain-text-length'
import { DEFAULT_TEXT_BOX_SIZE } from '@/lib/text/constants/text-defaults'
import type { Paragraph, TextChain } from '@/api/types/stowed-element'
import { buildCaptionParagraphs } from '@/lib/caption/build-caption-paragraphs'
import { resolveCaptionDefaultFont } from '@/lib/caption/resolve-caption-default-font'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import { paragraphsForWriteBack } from '../../helpers/paragraphs-for-write-back'
import { distributeAcrossChainBoundary } from '../../text-chain/distribute-across-chain-boundary'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomePersistence,
  HomeTextChains,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * Editing across linked text frames: caret hand-off between frames (Backspace, arrows, overflow),
 * linking, unlinking and creating a linked frame, repairing chains after a remote delete, and
 * Add Caption.
 */
export function useHomeChainEditing(
  deps: Pick<
    HomeFoundation,
    | 'textChains'
    | 'setTextChains'
    | 'shapeManagerRef'
    | 'fontFamiliesRef'
    | 'textChainsRef'
    | 'chainSeamMemoRef'
    | 'chainProjectionBaselineRef'
  > &
    Pick<HomeZoomAndSpread, 'spreadId'> &
    Pick<HomeDocumentLayout, 'bookSettingsPayload'> &
    Pick<HomeToolsAndSelection, 'candidAutoTaggingDataRef'> &
    Pick<
      HomePersistence,
      'pushAction' | 'markEdited' | 'checkpointDefaultFontName' | 'checkpointDefaultFontSize'
    > &
    Pick<
      HomeTextChains,
      | 'mintTextFrameUid'
      | 'buildChainStoryFrames'
      | 'applyStorySlices'
      | 'persistChainRider'
      | 'redistributeChainStory'
    >
) {
  const {
    textChains,
    setTextChains,
    shapeManagerRef,
    fontFamiliesRef,
    textChainsRef,
    chainSeamMemoRef,
    chainProjectionBaselineRef,
    spreadId,
    bookSettingsPayload,
    candidAutoTaggingDataRef,
    pushAction,
    markEdited,
    checkpointDefaultFontName,
    checkpointDefaultFontSize,
    mintTextFrameUid,
    buildChainStoryFrames,
    applyStorySlices,
    persistChainRider,
    redistributeChainStory,
  } = deps

  /**
   * Enter text edit on any chain member — Text-tool frame or shape converted to one — with the caret
   * at a character offset.
   *
   * The offset conventions of the two kinds are NOT interchangeable: a shape's flat text is
   * `paragraphsToPlainText` (paragraphs joined by `\n`, inner `textLines` with no separator, hidden
   * lines counted) while a text frame's is a DOM measurement that separates `textLines` with `\n` and
   * drops hidden lines. They disagree for any paragraph holding more than one `textLine`, which real
   * yb360 data has. Centralised here so no chain hand-off can pick the wrong one (WMPCM-9070).
   */
  const enterChainFrameEditAtOffset = useCallback(
    (frame: LinkableTextFrame, offset: 'start' | 'end' | number, paragraphs?: Paragraph[]) => {
      const smgr = shapeManagerRef.current
      const content = paragraphs ?? frame.paragraphs

      if (!isShapeBackedFrame(frame)) return false

      // `enterShapeTextEdit` silently no-ops on a hidden or locked shape; bail before anything
      // exits the current editor so the caret is never left nowhere.
      if (frame.hidden || frame.locked || !smgr) return false

      const caretOffset =
        offset === 'start'
          ? 0
          : offset === 'end'
            ? measureShapeParagraphsPlainTextLength(content)
            : offset

      smgr.enterShapeTextEdit(frame.id, { caretOffset })

      return true
    },
    [shapeManagerRef]
  )

  /** Settle pending debounced shape-text commits before moving the caret across frames. */
  const flushChainTextCommits = useCallback(() => {
    shapeManagerRef.current?.flushPendingShapeTextCommit()
  }, [shapeManagerRef])

  /** Every linkable frame on the spread, spanning both managers. */
  const collectChainFrames = useCallback(
    () => collectLinkableFrames(shapeManagerRef.current?.getShapes() ?? []),
    [shapeManagerRef]
  )

  /**
   * The linked frame before or after `frameId`, for a caret hand-off across the seam — `null` when
   * `frameId` is not a chain member, has no neighbour that way, or the neighbour is not on this spread.
   *
   * Settles pending debounced content commits before the editor moves away — otherwise that timer
   * later reads the wrong frame's edit-start baseline — and re-reads the neighbour AFTER the flush
   * rather than reusing a pre-flush snapshot: if the flush ever triggers a reflow that changes the
   * neighbour's content, computing/writing from a stale snapshot would silently discard it.
   */
  const crossToAdjacentChainFrame = useCallback(
    (
      frameId: string,
      direction: 'prev' | 'next'
    ): { frame: LinkableTextFrame; uid: string } | null => {
      const anchor = collectChainFrames().find((f) => f.id === frameId)

      if (!anchor || anchor.uid == null) return null

      const uid =
        direction === 'prev'
          ? prevUidInChain(textChains, String(anchor.uid))
          : nextUidInChain(textChains, String(anchor.uid))

      if (!uid) return null

      flushChainTextCommits()

      const frame = collectChainFrames().find((f) => f.uid != null && String(f.uid) === uid)

      return frame ? { frame, uid } : null
    },
    [collectChainFrames, flushChainTextCommits, textChains]
  )

  /**
   * Backward hand-off (WMPCM-6852): Backspace at the absolute start of a chain member with a
   * previous frame. Native editing does nothing here (nothing local to delete), so this pulls
   * exactly one character back from the end of the previous linked frame and moves the caret there —
   * mirroring what backspace would do if the two frames were one continuous field.
   *
   * Spans both managers (WMPCM-9070): either side of the boundary can be a shape acting as a text
   * frame, and before this the whole behavior simply didn't exist for shapes.
   */
  const handleChainBackspaceAtStart = useCallback(
    (frameId: string) => {
      const adjacent = crossToAdjacentChainFrame(frameId, 'prev')

      if (!adjacent) return

      const { frame: prev, uid: prevUid } = adjacent

      // `locked` only — pulling a character back writes `paragraphs`, never geometry, so a frozen
      // predecessor gives up its trailing character like any other frame (WMPCM-6980). Gating on
      // `frozen` here also skipped the caret hand-off below, leaving Backspace dead while ArrowLeft
      // (`handleChainArrowLeftAtStart`, which never checked freeze) crossed the same boundary fine.
      //
      // Now gates the CONTENT PULL alone. Bailing the whole handler took the caret hand-off down
      // with it, so a locked predecessor made Backspace dead at that seam for the same reason the
      // freeze check once did — ArrowLeft, which still checks nothing, crossed it fine either way.
      const nextParagraphs = canEditChainFrameContent(prev)
        ? deleteTrailingCharacter(prev.paragraphs)
        : prev.paragraphs
      const changed = JSON.stringify(nextParagraphs) !== JSON.stringify(prev.paragraphs)

      if (changed) {
        const historyTxnId = `textlink-backspace:${crypto.randomUUID()}`

        if (isShapeBackedFrame(prev)) {
          const shapeParagraphs = paragraphsForWriteBack(nextParagraphs)
          // Captured BEFORE the reflow below, which may make this frame's content longer by
          // reclaiming from downstream. The caret belongs where the deleted character was — the end
          // of the text as it stands right now — not at the end of whatever flows back in, so this
          // has to be a fixed number rather than a post-reflow `'end'`.
          const caretOffset = measureShapeParagraphsPlainTextLength(shapeParagraphs)

          // Flowed text again — a protected chain member must still receive it (WMPCM-6972 /
          // WMPCM-7052 AC3), which `shouldBlockShapeUpdate` would otherwise refuse on a locked frame.
          shapeManagerRef.current?.updateShape(
            prev.id,
            { paragraphs: shapeParagraphs },
            { bypassEditGuards: true }
          )
          pushAction({
            type: 'update',
            shapeId: prev.id,
            before: { paragraphs: prev.paragraphs },
            after: { paragraphs: shapeParagraphs },
            historyTxnId,
          })

          // Reflow the chain, which this handler used to skip entirely — it wrote `paragraphs`
          // straight to the manager and enqueued them itself, bypassing `handleShapeCommitted`, so
          // `willReflowChain` never fired. Two things went unfixed as a result (WMPCM-9070,
          // 2026-08-18 Bug 1):
          //
          // - the room the deleted character just freed was never reclaimed from downstream, leaving
          //   a visible gap mid-story — QA's "any character that no longer fits should reflow into
          //   text box 2 and remain visible";
          // - any OVERSET this frame was carrying stayed parked in it, invisible, instead of flowing
          //   onward. Overset is routine in a chain member now that over-capacity input is accepted
          //   rather than blocked (WMPCM-9338): normally the debounced commit's reflow pushes it on
          //   within 500 ms, but this was the one content-mutating path with no reflow behind it, so
          //   here it was frozen in place.
          //
          // The reclaim needs no opt-in and no pre-edit snapshot: the story is re-derived from the
          // frames as they now stand and re-split from the head, so the room the deleted character
          // freed is filled from downstream as a matter of course (D4). `forcePersistFrameIds`
          // guarantees this frame is written even when the distribution reports it unchanged.
          // Sharing `historyTxnId` makes the deletion and the redistribution it caused undo as one
          // step, exactly as `handleShapeCommitted` and its own reflow already do.
          redistributeChainStory(textChains, prevUid, prev.id, historyTxnId, {
            forcePersistFrameIds: new Set([prev.id]),
          })

          enterChainFrameEditAtOffset(prev, caretOffset)

          return
        }
      }

      // Nothing came back across the seam: the previous frame is drained to its style seed, or it is
      // locked. The chain still has to re-settle — the caret is crossing a boundary, and any overset
      // the frames on either side are parking should flow on rather than stay stuck. This path used
      // to return with no reflow at all, which is a large part of why the pull-up read as cascading
      // only sometimes: whether Backspace re-flowed the chain depended on whether this particular
      // frame happened to still have a character to give.
      //
      // No history action of its own is pushed here (nothing was deleted), but the reflow's own
      // actions still share the txn id, so if it does move content that move undoes as one step.
      if (isShapeBackedFrame(prev)) {
        // Captured BEFORE the reflow, for the same reason the pull branch above captures it: the
        // redistribution can lengthen this frame by reclaiming from downstream, and the caret belongs
        // at the seam the user backspaced into rather than after whatever flows back in.
        const caretOffset = measureShapeParagraphsPlainTextLength(
          paragraphsForWriteBack(nextParagraphs)
        )

        redistributeChainStory(
          textChains,
          prevUid,
          prev.id,
          `textlink-backspace:${crypto.randomUUID()}`,
          { forcePersistFrameIds: new Set([prev.id]) }
        )

        enterChainFrameEditAtOffset(prev, caretOffset)

        return
      }

      enterChainFrameEditAtOffset(prev, 'end', nextParagraphs)
    },
    [
      crossToAdjacentChainFrame,
      enterChainFrameEditAtOffset,
      pushAction,
      redistributeChainStory,
      textChains,
      shapeManagerRef,
    ]
  )

  /**
   * A shape text frame's caret ended up past what the shape can lay out, while it has a next frame:
   * follow the text into that frame — possibly several frames down the chain, if the same reflow
   * pushed content past more than one boundary at once (a large paste, or a font-size bump that
   * shrinks capacity across several linked frames in the same commit).
   *
   * Deliberately caret-only. The content movement is already owned by the debounced shape-text
   * commit, which runs `redistributeChainStory` and splits at the shape's real outline capacity —
   * adding a second immediate hand-off here would give the same content two writers that interleave.
   * So this flushes that commit (making the reflow happen NOW rather than in 500ms), then walks the
   * now-settled downstream frames via `locateChainCaretDestination` to find which one the offset
   * actually landed in, and places the caret there (WMPCM-9070).
   *
   * **Returns whether the edit session actually relocated** (WMPCM-9212). Every bail-out below is a
   * state this cannot resolve — an unlinked frame, a chain the anchor is not in, a downstream member
   * that is not on this spread — and the caller has to be able to tell that apart from a completed
   * hand-off. `ShapeTextEditor` latched its hand-off attempt as done on the bare fact that it called
   * this, so any silent bail left the caret in the source frame with no way to try again for the rest
   * of the edit session: every later keystroke was re-inserted at the same offset and re-evicted to
   * the HEAD of the next frame, which the user saw as their text arriving in reverse order.
   */
  const handleShapeChainOverflowCaret = useCallback(
    (shapeId: string, localCaretOffset: number): boolean => {
      const anchor = collectChainFrames().find((f) => f.id === shapeId)

      if (!anchor || anchor.uid == null) return false

      const anchorUid = String(anchor.uid)
      const nextUid = nextUidInChain(textChains, anchorUid)

      if (!nextUid) return false

      // Must run before the caret moves: this is what actually flows the tail into the next frame(s).
      flushChainTextCommits()

      const chain = chainContaining(textChains, anchorUid)

      if (!chain) return false

      // Every member from the immediate next frame onward, in chain order — the hand-off content
      // may have cascaded past any number of them in this one flush.
      const downstreamUids = chainMembersFrom(chain, nextUid).map((tb) => tb.uid)
      const liveFrames = collectChainFrames()
      const chainCaretFrames: ChainCaretFrame[] = downstreamUids
        .map((uid) => liveFrames.find((f) => f.uid != null && String(f.uid) === uid))
        .filter((f): f is LinkableTextFrame => f != null)
        .map((f) => ({ id: f.id, paragraphs: f.paragraphs }))

      const destination = locateChainCaretDestination(chainCaretFrames, localCaretOffset)

      if (!destination) return false

      const next = liveFrames.find((f) => f.id === destination.frameId)

      if (!next) return false

      return enterChainFrameEditAtOffset(next, destination.offset)
    },
    [collectChainFrames, enterChainFrameEditAtOffset, flushChainTextCommits, textChains]
  )

  /**
   * ArrowLeft at the very start of a chain member with a previous frame: pure caret hand-off to
   * the end of the previous linked frame — mirrors `handleChainBackspaceAtStart`'s navigation but
   * without pulling a character across the boundary (no content mutation, no undo entry).
   */
  const handleChainArrowLeftAtStart = useCallback(
    (frameId: string) => {
      const adjacent = crossToAdjacentChainFrame(frameId, 'prev')

      if (!adjacent) return

      enterChainFrameEditAtOffset(adjacent.frame, 'end')
    },
    [crossToAdjacentChainFrame, enterChainFrameEditAtOffset]
  )

  /**
   * ArrowRight at the very end of a chain member with a next frame: pure caret hand-off to the
   * start of the next linked frame — the symmetric counterpart to `handleChainArrowLeftAtStart`.
   */
  const handleChainArrowRightAtEnd = useCallback(
    (frameId: string) => {
      const adjacent = crossToAdjacentChainFrame(frameId, 'next')

      if (!adjacent) return

      enterChainFrameEditAtOffset(adjacent.frame, 'start')
    },
    [crossToAdjacentChainFrame, enterChainFrameEditAtOffset]
  )

  /**
   * Repairs local chain topology after a text box was deleted by a REMOTE editor (WMPCM-6868).
   * `useMultiplayerElementsSync` already removes the box from TextManager but has no access to
   * `textChains`, which lives here. Splices the uid out of whatever chain it belonged to so a
   * later local edit's `redistributeChainStory` doesn't re-send a dangling reference. Uses the
   * functional form of `setTextChains` (not the `textChains` closure) so this is correct
   * regardless of render timing. Not pushed to undo history (mirrors a remote mutation, not a
   * local action) and intentionally does not re-persist — the deleting client's own delete
   * handler already persisted the repaired chain topology; this only fixes this client's stale
   * in-memory copy.
   */
  const handleRemoteTextBoxDeleted = useCallback(
    (_objectId: string, uid?: number) => {
      // Chain membership keys off the numeric wire uid, not the objectId UUID — without this, the
      // splice below was a no-op for every remote delete (WMPCM-6868).
      if (uid == null) return
      // Advance the live mirror too, so a local delete repair landing later in this same tick reads
      // the post-splice topology rather than re-introducing the remotely deleted uid.
      textChainsRef.current = removeBoxFromChains(textChainsRef.current, String(uid))
      setTextChains((prev) => removeBoxFromChains(prev, String(uid)))
    },
    [setTextChains, textChainsRef]
  )

  /**
   * Looks up a linkable frame by object id across both managers — a Text-tool frame or a shape
   * converted to a text frame (WMPCM-7988).
   */
  const findLinkableFrame = useCallback(
    (frameId: string): LinkableTextFrame | undefined => {
      return collectLinkableFrames(shapeManagerRef.current?.getShapes() ?? []).find(
        (f) => f.id === frameId
      )
    },
    [shapeManagerRef]
  )

  /**
   * Returns the frame's persisted uid, allocating one for legacy/new frames that lack it.
   * `assigned: true` means a uid was just minted this call — callers must force-persist that frame
   * (via `persistChainRider`'s force list) since a freshly-assigned uid living only
   * in the local manager is silently dropped from the save payload otherwise (WMPCM-6868).
   */
  const ensureFrameUid = useCallback(
    (frame: LinkableTextFrame): { uid: number; assigned: boolean } => {
      if (typeof frame.uid === 'number' && frame.uid > 0) return { uid: frame.uid, assigned: false }
      const uid = mintTextFrameUid()

      if (isShapeBackedFrame(frame)) {
        shapeManagerRef.current?.updateShape(frame.id, { uid })
      }

      return { uid, assigned: true }
    },
    [mintTextFrameUid, shapeManagerRef]
  )

  const handleTextFramesLinked = useCallback(
    (
      sourceId: string,
      targetId: string,
      historyTxnIdOverride?: string,
      chainsOverride?: TextChain[]
    ): TextChain[] | undefined => {
      const sourceFrame = findLinkableFrame(sourceId)
      const targetFrame = findLinkableFrame(targetId)

      if (!sourceFrame || !targetFrame) return undefined

      const source = ensureFrameUid(sourceFrame)
      const target = ensureFrameUid(targetFrame)
      const sourceUid = String(source.uid)
      const targetUid = String(target.uid)
      // A caller linking several pairs in one synchronous pass (Mirror Copy relinking a 3+-member
      // chain) passes the PREVIOUS call's returned chains here instead of the `textChains` state
      // closure, which doesn't advance mid-handler — without this, each pairwise link would be
      // computed against the same stale "before" snapshot and only the last `setTextChains` call
      // would stick, silently dropping every interior link (WMPCM-9233 follow-up).
      const chainsBeforeThisCall = chainsOverride ?? textChains
      const error = canLink(
        chainsBeforeThisCall,
        {
          uid: sourceUid,
          isTextFrame: true,
          locked: sourceFrame.locked,
          frozen: sourceFrame.frozen,
          spreadId: sourceFrame.spreadId,
          parentGroupId: sourceFrame.parentGroupId,
        },
        {
          uid: targetUid,
          isTextFrame: true,
          locked: targetFrame.locked,
          frozen: targetFrame.frozen,
          spreadId: targetFrame.spreadId,
          parentGroupId: targetFrame.parentGroupId,
        }
      )

      if (error) return undefined

      const chainsBefore = chainsBeforeThisCall
      const chainsAfter = addLink(chainsBeforeThisCall, sourceUid, targetUid, TEXT_LINK_LAYER)
      // A caller that already opened its own undo transaction (e.g. Mirror Copy linking its
      // freshly-cloned pair) passes that id through so this link's actions join it as one
      // compound undo step, instead of starting a second, separately-undoable step right after.
      const historyTxnId = historyTxnIdOverride ?? `textlink:${crypto.randomUUID()}`
      // Force-persist any box whose uid was just minted this call — otherwise a box that isn't
      // the rider and whose paragraphs don't change would silently drop its new uid from the save
      // payload, and the whole link disappears on reload (WMPCM-6868).
      const forcePersistBoxIds = new Set(
        [source.assigned ? sourceId : null, target.assigned ? targetId : null].filter(
          (id): id is string => id != null
        )
      )

      setTextChains(chainsAfter)
      pushAction({
        type: 'textLinkChange',
        chainsBefore,
        chainsAfter,
        riderTextBoxId: sourceId,
        historyTxnId,
      })
      // D3 — linking flows FORWARD only. The target keeps its own text; nothing is pulled up into
      // the source. But an overflowing source still pushes its overset into the target, which is the
      // main reason anyone links two frames at all — "nothing flows" would break the feature.
      //
      // Under the story model that needs no marker. `separateStoryAfterFrameId` was a per-reflow flag
      // meaning "drain nothing past this frame"; here it is the same split-distribute primitive
      // unlink uses, with the source side distributed first and its remainder leading the target's
      // own content.
      //
      // **Forward-only applies at THIS gesture only.** The next trigger anywhere in the chain
      // redistributes the whole story head-first (D4), so a half-full source pulls the target's text
      // up on the first keystroke after the link. That delayed jump is the shape of WMPCM-6964; it is
      // deliberately one isolated switch at one call site, so reversing the ruling needs no redesign.
      const mergedForFlow = chainContaining(chainsAfter, sourceUid)
      const mergedMembers = mergedForFlow?.textBox ?? []
      const boundary = mergedMembers.findIndex((tb) => tb.uid === sourceUid)
      const upstream = buildChainStoryFrames(mergedMembers.slice(0, boundary + 1))
      const downstream = buildChainStoryFrames(mergedMembers.slice(boundary + 1))
      const seamMemo = chainSeamMemoRef.current
      const downstreamStory = storyFromChain(downstream, chainSeamsFromMemo(seamMemo, downstream))
      const linkWindow = [...upstream, ...downstream]
      const { slices: linkSlices, seams: linkSeams } = distributeAcrossChainBoundary(
        upstream,
        storyFromChain(upstream, chainSeamsFromMemo(seamMemo, upstream)),
        downstream,
        // A downstream run holding nothing but style seeds contributes no content. Passing the seed
        // through would append a blank paragraph after the incoming text and consume a row of the
        // target's height, so a frame that previously fit could start overflowing purely from being
        // linked (WMPCM-6964).
        paragraphsHaveText(downstreamStory) ? downstreamStory : []
      )

      recordChainSeams(seamMemo, linkWindow, linkSlices, linkSeams)

      // This gesture splits one story into two and writes its own `before` from the live frames, so
      // every pre-projection baseline is retired here rather than carried across the topology change.
      chainProjectionBaselineRef.current.clear()

      const linkChanged = changedSlices(linkWindow, linkSlices)
      const linkChangedIds = linkChanged.map((slice) => slice.id)

      applyStorySlices(linkWindow, linkChanged, historyTxnId)

      if (linkChangedIds.length > 0) void markEdited(linkChangedIds)

      // Force-persists any box whose uid was just minted this call — see `forcePersistBoxIds`.
      persistChainRider(
        chainsAfter,
        sourceId,
        historyTxnId,
        new Set([...linkChangedIds, ...forcePersistBoxIds])
      )

      // AC: linking selects the LAST frame in the resulting chain — not necessarily `targetId`
      // itself, since target may already have been the head of its own sub-chain with successors.
      const mergedChain = chainContaining(chainsAfter, targetUid)
      const lastUid = mergedChain?.textBox[mergedChain.textBox.length - 1]?.uid
      const lastFrame =
        lastUid != null
          ? collectLinkableFrames(shapeManagerRef.current?.getShapes() ?? []).find(
              (f) => String(f.uid) === lastUid
            )
          : undefined
      const frameToSelect = lastFrame ?? findLinkableFrame(targetId)

      // One manager owns every frame now, so there is no second selection to clear — `dev`'s
      // branch here existed only to keep a mixed shape/TextManager chain from looking
      // double-selected (WMPCM-7988).
      shapeManagerRef.current?.selectShapes([frameToSelect?.id ?? targetId])

      // Mirror Copy relinks a cloned chain one consecutive pair at a time and must thread each
      // call's result into the next (`chainsOverride`) — `textChains` state doesn't advance
      // mid-handler. Returning the new chains is what makes a 3+ member cloned chain keep every
      // link instead of only its last (WMPCM-9233).
      return chainsAfter
    },
    [
      applyStorySlices,
      buildChainStoryFrames,
      ensureFrameUid,
      findLinkableFrame,
      markEdited,
      persistChainRider,
      pushAction,
      textChains,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
      setTextChains,
      shapeManagerRef,
    ]
  )

  const handleTextFramesUnlinked = useCallback(
    (frameId: string) => {
      const shape = shapeManagerRef.current?.getShapes().find((s) => s.id === frameId)

      if (!shape || shape.uid == null) return

      const frameUid = shape.uid

      const uid = String(frameUid)
      const chainsBefore = textChains
      const chainsAfter = removeLinkAfter(textChains, uid)

      if (chainsAfter === chainsBefore) return

      const historyTxnId = `textunlink:${crypto.randomUUID()}`

      setTextChains(chainsAfter)
      pushAction({
        type: 'textLinkChange',
        chainsBefore,
        chainsAfter,
        riderTextBoxId: frameId,
        historyTxnId,
      })

      // D8 — unlink reflows BOTH resulting stories. Until this call the chain was one story, so it is
      // assembled from the pre-unlink membership and re-split at the cut: the upstream side keeps as
      // much as fits (frame 1 pulls back) and the remainder becomes the detached tail's own story.
      // Before this, unlink changed topology and never reflowed at all, orphaning text in the tail.
      //
      // Done here rather than by calling `redistributeChainStory` afterwards: `removeLinkAfter` drops
      // any chain left with fewer than two members, so after unlinking a 2-frame chain neither side
      // has a `TextChain` left to look up and the redistribution would bail at `chainContaining`.
      // That is also why the story window is keyed off `chainsBefore`.
      const unlinkedChain = chainContaining(chainsBefore, uid)
      const unlinkWindow = buildChainStoryFrames(unlinkedChain?.textBox ?? [])
      const cut = unlinkWindow.findIndex((f) => f.id === frameId)
      let unlinkChangedIds: string[] = []

      if (cut >= 0 && cut < unlinkWindow.length - 1) {
        const seamMemo = chainSeamMemoRef.current
        const { slices: unlinkSlices, seams: unlinkSeams } = distributeAcrossChainBoundary(
          unlinkWindow.slice(0, cut + 1),
          storyFromChain(unlinkWindow, chainSeamsFromMemo(seamMemo, unlinkWindow)),
          unlinkWindow.slice(cut + 1),
          [],
          // The severed tail is a fresh, separate story — not a destination for what the upstream
          // side can no longer display. It starts empty; the upstream side keeps the rest as
          // ordinary overset (WMPCM-10398).
          { carryUpstreamOverflowForward: false }
        )

        // The cut boundary is gone: the two sides are separate stories from this moment, so its
        // entry must be DELETED rather than recorded. `undefined` is what tells `recordChainSeams`
        // to do that.
        unlinkSeams[cut] = undefined
        recordChainSeams(seamMemo, unlinkWindow, unlinkSlices, unlinkSeams)
        // Same as the link path: the cut retires every pre-projection baseline rather than letting one
        // pair a pre-cut `before` with a post-cut slice.
        chainProjectionBaselineRef.current.clear()

        const unlinkChanged = changedSlices(unlinkWindow, unlinkSlices)

        applyStorySlices(unlinkWindow, unlinkChanged, historyTxnId)
        unlinkChangedIds = unlinkChanged.map((slice) => slice.id)
      }

      void markEdited([frameId, ...unlinkChangedIds])
      persistChainRider(chainsAfter, frameId, historyTxnId, new Set(unlinkChangedIds))
    },
    [
      applyStorySlices,
      buildChainStoryFrames,
      markEdited,
      persistChainRider,
      pushAction,
      textChains,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
      setTextChains,
      shapeManagerRef,
    ]
  )

  const handleCreateLinkedTextFrame = useCallback(
    ({
      sourceId,
      docPoint,
      size,
    }: {
      sourceId: string
      docPoint: { x: number; y: number }
      size?: { width: number; height: number }
    }) => {
      const mgr = shapeManagerRef.current
      // The source may be a Text-tool frame or a shape converted to a text frame (WMPCM-7988), and
      // the frame created MIRRORS IT (WMPCM-6884): same outline, fill and border. Both are
      // ShapeManager shapes now, so one builder and one `addShapeData` cover every case.
      const sourceFrame = findLinkableFrame(sourceId)

      if (!mgr || !sourceFrame) return

      // Defense in depth: the out-port UI already hides this gesture once a frame is locked or
      // already has an outgoing link, but re-check at commit time (canLink's rules) in case that
      // state changed during the linking-mode window (e.g. a remote edit) — WMPCM-6852.
      if (sourceFrame.locked) return
      if (sourceFrame.uid != null && hasOutgoingLink(textChains, String(sourceFrame.uid))) return
      // Narrows `sourceFrame.shape` for the clone below, and states the invariant explicitly rather
      // than leaving it implied: every linkable frame is shape-backed since text-unification.
      if (!isShapeBackedFrame(sourceFrame)) return

      const newUid = mintTextFrameUid()
      // The target MIRRORS its source (WMPCM-6884): same outline, fill and border. One builder
      // covers every case because `collectLinkableFrames` is called with an empty text-box list
      // everywhere now — a Text-tool frame IS a shape — so `findLinkableFrame` only ever yields
      // shape-backed frames and `isShapeBackedFrame(sourceFrame)` is unconditionally true. Branching
      // on it would read as a live choice while only one arm could ever run.
      //
      // Always normal and unfrozen — freeze is never inherited from the source (WMPCM-6972).
      const newBox: ShapeData = buildLinkedShapeFrame({
        sourceShape: sourceFrame.shape,
        docPoint,
        uid: newUid,
        fallbackSpreadId: spreadId,
      })

      const source = ensureFrameUid(sourceFrame)
      const sourceUid = String(source.uid)
      const targetUid = String(newUid)
      const chainsBefore = textChains
      const chainsAfter = addLink(textChains, sourceUid, targetUid, TEXT_LINK_LAYER)
      const historyTxnId = `textlink:${crypto.randomUUID()}`
      // The source may have just had a uid minted too (legacy frame) — force-persist it the same
      // way as handleTextFramesLinked; newBox itself is always persisted via addShapeData below.
      const forcePersistBoxIds = new Set(source.assigned ? [sourceId] : [])
      // addShapeData mints the real (dash-free hex) id and fires onShapeCreated
      // (handleShapeCreated) synchronously — the same path any other new shape goes through:
      // assigns a real fractionalIndex, appends to unifiedLayerIds, marks edited, pushes the 'add'
      // undo action, and enqueues the add. No separate 'textAdd' push or manual enqueueAdd needed
      // (contrast the old TextManager-routed version of this). Its `enqueueAdd` carries no chain
      // rider, but `persistChainRider`'s `enqueueUpdate` does, and ChangeQueue
      // coalesces add+update on one objectId while keeping the update's `linkedTextElements`.
      //
      // A drag-sized gesture supplies `size`. `buildLinkedShapeFrame` deliberately does not apply it
      // itself, so that the manager's size-change branch rescales `pathCommands` and regenerates the
      // SVG path (preserving open/closed state for pen paths) — but it is handed to `addShapeData`
      // rather than applied by a follow-up `updateShape`, because `onShapeCreated` takes the history
      // snapshot synchronously inside this call. Resizing after it returns left the 'add' action
      // holding the SOURCE's dimensions, so redo re-created the frame at the wrong size and
      // re-persisted it (`applyHistoryPersistencePlan` prefers `upsert.shape` over live state).
      // It also has to be in place before the redistribution, which derives the frame's
      // capacity and re-enqueues it from the LIVE shape.
      const created = mgr.addShapeData(newBox, {
        selectAfterCreate: false,
        meta: { historyTxnId },
        ...(size ? { size } : {}),
      })
      const newFrameId = created.id

      setTextChains(chainsAfter)
      pushAction({
        type: 'textLinkChange',
        chainsBefore,
        chainsAfter,
        riderTextBoxId: newFrameId,
        historyTxnId,
      })

      // The plain head-first redistribution, not the forward-only link split `handleTextFramesLinked`
      // uses: a brand-new frame has no story of its own to protect. Its only content is the style seed
      // from `buildLinkedFrameStyleSeed`, and `storyFromChain` skips a frame carrying no text — so the
      // seed never joins the source's story and can never consume a row of the SOURCE's height, which
      // is what would let a frame that previously fit start overflowing purely from being linked
      // (WMPCM-6964). `sliceOrSeed` rebuilds the seed from the frame's own typography either way.
      redistributeChainStory(chainsAfter, sourceUid, newFrameId, historyTxnId, {
        forcePersistFrameIds: forcePersistBoxIds,
      })
      // `newFrameId`, never `newBox.id`: `buildLinkedShapeFrame` deliberately leaves `id` empty so
      // `addShapeData` can mint the dash-free hex GUID the backend requires of client-generated
      // shape ids, so the pre-add object's id is meaningless on the clone path.
      mgr.selectShapes([newFrameId])
    },
    [
      ensureFrameUid,
      findLinkableFrame,
      mintTextFrameUid,
      pushAction,
      redistributeChainStory,
      spreadId,
      textChains,
      setTextChains,
      shapeManagerRef,
    ]
  )

  const handleAddCaption = useCallback(() => {
    const mgr = shapeManagerRef.current

    if (!mgr) return

    const selectedShapeIds = mgr.getSelectedShapeIds()

    if (selectedShapeIds.length !== 1) return

    const photoShape = mgr
      .getShapes()
      .find((s) => s.id === selectedShapeIds[0] && resolvePlacedPhotoIdentity(s))

    if (!photoShape) return

    // captionForShapeId is session-only (never persisted, Known Gap #12 in CLAUDE.md), so this
    // only ever matches a caption added earlier in the same session.
    const existingCaptionShape = mgr.getShapes().find((s) => s.captionForShapeId === photoShape.id)

    if (existingCaptionShape) {
      mgr.selectShapes([existingCaptionShape.id])
      mgr.enterShapeTextEdit(existingCaptionShape.id)
      return
    }

    const defaultFontStyle = resolveCaptionDefaultFont({
      bookDefaultFontFamilyId: bookSettingsPayload?.job?.defaultFontFamilyId,
      bookDefaultFontSize: bookSettingsPayload?.job?.defaultFontSize,
      checkpointDefaultFontName,
      checkpointDefaultFontSize,
      families: fontFamiliesRef.current,
    })
    const paragraphs = buildCaptionParagraphs(candidAutoTaggingDataRef.current, defaultFontStyle)

    const captionBox: ShapeData = {
      id: crypto.randomUUID(),
      captionForShapeId: photoShape.id,
      x: photoShape.x,
      y: photoShape.y + photoShape.height,
      width: photoShape.width,
      height: DEFAULT_TEXT_BOX_SIZE.height,
      type: 'square',
      kindId: 1,
      paragraphs,
      rotation: 0,
      // ShapeRenderer defaults an unset fill/stroke to a visible gray/black rectangle — wrong for
      // a text frame (see checkpoint-to-text.ts, build-linked-text-frame.ts for the same fix).
      fill: 'transparent',
      stroke: 'transparent',
      strokeWidth: 0,
    }

    // addShapeData fires onShapeCreated (handleShapeCreated) synchronously — the same
    // fractionalIndex/unifiedLayerIds/undo/persistence path any other new shape goes through.
    mgr.selectNone()
    mgr.addShapeData(captionBox, { selectAfterCreate: false })
    mgr.enterShapeTextEdit(captionBox.id)
  }, [
    bookSettingsPayload?.job?.defaultFontFamilyId,
    bookSettingsPayload?.job?.defaultFontSize,
    checkpointDefaultFontName,
    checkpointDefaultFontSize,
    fontFamiliesRef,
    shapeManagerRef,
    candidAutoTaggingDataRef,
  ])

  return {
    enterChainFrameEditAtOffset,
    flushChainTextCommits,
    collectChainFrames,
    handleChainBackspaceAtStart,
    handleShapeChainOverflowCaret,
    handleChainArrowLeftAtStart,
    handleChainArrowRightAtEnd,
    handleRemoteTextBoxDeleted,
    findLinkableFrame,
    ensureFrameUid,
    handleTextFramesLinked,
    handleTextFramesUnlinked,
    handleCreateLinkedTextFrame,
    handleAddCaption,
  }
}
